import apiClient from '@/lib/api';

export interface AdminXrayPlan {
  id: number;
  name: string;
  displayName: string;
  displayPrice: number | null;
  stripePriceId: string | null;
  /** The plan's monthly fee in cents. Charged; never added to a balance. */
  monthlyFeeCents: number;
  /** Free Xray Enrich lookups the plan includes each month. */
  includedEnrichUnits: number;
}

/** A catalog product. Its prices live per plan, on the pricing grid. */
export interface AdminXrayProduct {
  id: number;
  key: string;
  name: string;
  sortOrder: number;
}

/** One cell of the pricing grid: what a product costs per lead on a plan. */
export interface AdminXrayPlanPrice {
  planId: number;
  productId: number;
  priceCents: number;
}

/** The whole grid: rows are products, columns are plans, cells are prices. */
export interface AdminXrayPricingGrid {
  products: AdminXrayProduct[];
  plans: Array<{
    id: number;
    name: string;
    displayName: string;
    monthlyFeeCents: number;
    includedEnrichUnits: number;
  }>;
  prices: AdminXrayPlanPrice[];
}

export interface AdminRoasPlanBonus {
  id: number;
  name: string;
  displayName: string;
  /** One-time welcome funds added to the wallet on first paid subscribe, in cents. */
  freeXrayCents: number;
}

/** Mirrors the backend XrayWallet. Money is cents throughout. */
export interface AdminXrayWallet {
  status: string;
  runnable: boolean;
  plan: {
    name: string;
    displayName: string;
    displayPrice: number | null;
    monthlyFeeCents: number;
    includedEnrichUnits: number;
    prices: Record<string, number>;
  };
  /** The plan's free Xray Enrich allowance this period. Not money. */
  includedEnrich: { perPeriod: number; remaining: number; used: number };
  balanceCents: number;
  totalCents: number;
  spentThisPeriod: {
    totalCents: number;
    byProduct: Record<string, { cents: number; count: number; freeCount: number }>;
  };
  autoRecharge: {
    enabled: boolean;
    thresholdCents: number;
    amountCents: number;
    monthlyCapCents: number;
    rechargedThisMonthCents: number;
    capReached: boolean;
  };
  cancelAtPeriodEnd: boolean;
  periodStartUtc: string;
  periodEndUtc: string;
  pastDueSinceUtc: string | null;
  gracePeriodEndsAtUtc: string | null;
}

export interface AdminAgencyXraySummary {
  hasSubscription: boolean;
  xrayPlan?: {
    name: string;
    displayName: string;
    displayPrice: number | null;
    monthlyFeeCents: number;
    includedEnrichUnits: number;
    prices: Record<string, number>;
  };
  roasPlan?: { name: string; displayName: string; freeXrayCents: number } | null;
  wallet?: AdminXrayWallet;
}

// =========================================================================
// Overview page
// =========================================================================

/** A complaint or breach report in the intake queue. */
export interface AdminXrayComplaint {
  publicId: string;
  agency: { publicId: string; name: string } | null;
  /** consumer | regulator | customer_forwarded | data_provider | breach */
  source: string;
  consumerIdentifier: string | null;
  summary: string;
  /** open | investigating | forwarded | resolved */
  status: string;
  receivedAtUtc: string;
  forwardedAtUtc: string | null;
  resolvedAtUtc: string | null;
  notes: string | null;
  businessDaysElapsed: number;
  /** Past its SLA: five business days, or 72 hours for a breach. */
  forwardOverdue: boolean;
  /** Breaches only: when LeadFi must have been notified by. */
  notifyDeadlineUtc: string | null;
  hoursElapsed: number | null;
}

export interface AdminXrayOverviewStats {
  // Snapshot. activeSubs includes free trials; paidSubs and mrr do not.
  activeSubs: number;
  paidSubs: number;
  mrr: number;
  // Range-bounded
  topupRevenueCents: number;
  totalLookups: number;
  billableLookups: number;
  matchRate: number;
  autoRechargeCount: number;
  periodComparison: {
    topupRevenueChange: string;
    totalLookupsChange: string;
    billableLookupsChange: string;
    matchRateChange: string;
    autoRechargeCountChange: string;
  };
}

export interface AdminXrayChartPoint {
  date: string;
  lookups: number;
  billableLookups: number;
  topupRevenue: number;
  subMrrAdded: number;
}

export interface AdminXrayMappedChartPoint {
  name: string;
  fullDate: string;
  value: number;
}

export type XrayChartMetric = "lookups" | "billableLookups" | "topupRevenue" | "subMrrAdded";

/** One chart point per day for the selected metric, labelled in the viewer's time zone. */
export function mapXrayChartRows(
  rows: AdminXrayChartPoint[],
  metric: XrayChartMetric,
  userTimezone?: string,
): AdminXrayMappedChartPoint[] {
  const tz = userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  return rows.map((item) => {
    const date = new Date(item.date);
    const name = date.toLocaleDateString("en-US", { timeZone: tz, month: "short", day: "numeric" });
    const fullDate = date.toLocaleDateString("en-US", {
      timeZone: tz,
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return { name, fullDate, value: Number(item[metric] ?? 0) };
  });
}

export interface AdminXrayRecentSub {
  id: string;
  agencyPublicId: string;
  agencyName: string;
  plan: string;
  planName: string;
  displayPrice: number | null;
  status: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
}

export interface AdminXrayRecentTopup {
  id: string;
  agencyPublicId: string;
  agencyName: string;
  amountCents: number;
  /** manual | auto | admin_gift */
  triggerType: string;
  reason: string | null;
  createdAt: string;
}

export interface AdminXrayTopAgency {
  agencyPublicId: string | null;
  agencyName: string;
  plan: string;
  planName: string | null;
  /** Credit checks charged in the period. */
  creditChecks: number;
  /** What those lookups were charged, in cents. */
  spentCents: number;
}

export interface StripePriceInfo {
  id: string;
  active: boolean;
  currency: string;
  unitAmount: number | null;
  type: string;
  interval: string | null;
  intervalCount: number | null;
  product: { id: string; name: string; active: boolean } | null;
}


export interface AdminXrayTermsAcceptance {
  publicId: string;
  agency: { publicId: string; name: string } | null;
  /** The subaccount whose business it certifies for; null once deleted. */
  subaccount: { publicId: string; name: string } | null;
  userName: string;
  userEmail: string;
  assertedRole: string;
  termsVersion: string;
  termsSha256: string;
  /** False when this row attests to text other than what this build serves. */
  isCurrent: boolean;
  acceptedAtUtc: string;
  ipAddress: string | null;
  /** The acceptance this one reused from another subaccount; null for one made in person. */
  reusedFrom: string | null;
}

export interface AdminXrayTerms {
  version: string;
  sha256: string;
  text: string;
  acceptances: AdminXrayTermsAcceptance[];
}

export const xrayAdminService = {
  // Plans
  listPlans: async (): Promise<AdminXrayPlan[]> => {
    const res = await apiClient.get<{ data: AdminXrayPlan[] }>('/admin/xray/plans');
    return res.data.data;
  },
  updatePlan: async (id: number, body: Partial<Omit<AdminXrayPlan, 'id' | 'name'>>): Promise<AdminXrayPlan> => {
    const res = await apiClient.patch<{ data: AdminXrayPlan }>(`/admin/xray/plans/${id}`, body);
    return res.data.data;
  },
  testStripePrice: async (priceId: string): Promise<StripePriceInfo> => {
    const res = await apiClient.post<{ data: StripePriceInfo }>('/admin/xray/test-stripe-price', { priceId });
    return res.data.data;
  },

  // Pricing grid (products x plans, per-lead price in cents)
  getPricingGrid: async (): Promise<AdminXrayPricingGrid> => {
    const res = await apiClient.get<{ data: AdminXrayPricingGrid }>('/admin/xray/prices');
    return res.data.data;
  },
  updatePrices: async (updates: AdminXrayPlanPrice[]): Promise<AdminXrayPlanPrice[]> => {
    const res = await apiClient.patch<{ data: AdminXrayPlanPrice[] }>('/admin/xray/prices', { updates });
    return res.data.data;
  },

  // ROAS plan welcome funds (cents)
  listRoasBonus: async (): Promise<AdminRoasPlanBonus[]> => {
    const res = await apiClient.get<{ data: AdminRoasPlanBonus[] }>('/admin/xray/roas-bonus');
    return res.data.data;
  },
  updateRoasBonus: async (updates: { id: number; freeXrayCents: number }[]): Promise<AdminRoasPlanBonus[]> => {
    const res = await apiClient.patch<{ data: AdminRoasPlanBonus[] }>('/admin/xray/roas-bonus', { updates });
    return res.data.data;
  },

  // Per-agency
  getAgencySummary: async (agencyPublicId: string): Promise<AdminAgencyXraySummary> => {
    const res = await apiClient.get<{ data: AdminAgencyXraySummary }>(`/admin/xray/agencies/${agencyPublicId}`);
    return res.data.data;
  },
  grantFunds: async (agencyPublicId: string, amountCents: number, reason?: string) => {
    const res = await apiClient.post<{ data: { addedCents: number; balanceCents: number; totalCents: number } }>(
      `/admin/agencies/${agencyPublicId}/wallet/grant-funds`,
      reason ? { amountCents, reason } : { amountCents },
    );
    return res.data.data;
  },

  // Overview
  getOverviewStats: async (range: string = "1m"): Promise<AdminXrayOverviewStats> => {
    const res = await apiClient.get<{ data: AdminXrayOverviewStats }>("/admin/xray/overview/stats", { params: { range } });
    return res.data.data;
  },
  // Every metric for every day; the page maps the selected metric with
  // mapXrayChartRows, so switching metrics needs no request.
  getOverviewChartRows: async (range: string = "1m"): Promise<AdminXrayChartPoint[]> => {
    const res = await apiClient.get<{ data: AdminXrayChartPoint[] }>("/admin/xray/overview/chart", { params: { range } });
    return res.data.data;
  },
  getRecentSubs: async (): Promise<AdminXrayRecentSub[]> => {
    const res = await apiClient.get<{ data: AdminXrayRecentSub[] }>("/admin/xray/overview/recent-subs");
    return res.data.data;
  },
  getRecentTopups: async (): Promise<AdminXrayRecentTopup[]> => {
    const res = await apiClient.get<{ data: AdminXrayRecentTopup[] }>("/admin/xray/overview/recent-topups");
    return res.data.data;
  },
  getTopAgencies: async (range: string = "1m"): Promise<AdminXrayTopAgency[]> => {
    const res = await apiClient.get<{ data: AdminXrayTopAgency[] }>("/admin/xray/overview/top-agencies", { params: { range } });
    return res.data.data;
  },

  // -----------------------------------------------------------------------
  // Compliance: complaints and evidence exports
  // -----------------------------------------------------------------------

  getComplaints: async (status?: string): Promise<AdminXrayComplaint[]> => {
    const res = await apiClient.get<{ data: AdminXrayComplaint[] }>("/admin/xray/complaints", {
      params: status ? { status } : {},
    });
    return res.data.data;
  },

  createComplaint: async (input: {
    source: string;
    summary: string;
    consumerIdentifier?: string;
    agencyPublicId?: string;
    receivedAtUtc?: string;
    notes?: string;
  }): Promise<{ publicId: string }> => {
    const res = await apiClient.post<{ data: { publicId: string } }>("/admin/xray/complaints", input);
    return res.data.data;
  },

  updateComplaint: async (publicId: string, patch: { status?: string; notes?: string }): Promise<void> => {
    await apiClient.patch("/admin/xray/complaints", { publicId, ...patch });
  },

  /**
   * Download an evidence export. Goes through the API client so the admin auth
   * header is applied, then hands the browser a blob: the response is NDJSON
   * with a content-disposition, and a plain link would be unauthenticated.
   */
  downloadExport: async (params: {
    agencyPublicId: string;
    kind: "acceptances" | "consent" | "suppressions";
    from?: string;
    to?: string;
  }): Promise<{ records: number }> => {
    const res = await apiClient.get("/admin/xray/export", { params, responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/x-ndjson" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `xray-${params.kind}-agency-${params.agencyPublicId}.ndjson`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return { records: Number(res.headers["x-record-count"] ?? 0) };
  },

  /** The terms this build serves, and who has accepted what. Read-only. */
  getTerms: async (): Promise<AdminXrayTerms> => {
    const res = await apiClient.get<{ data: AdminXrayTerms }>('/admin/xray/terms');
    return res.data.data;
  },
};
